import { formatDate } from '@angular/common';

import {
  CueDateTimePickerDisabledDates,
  CueDateTimePickerFormatPlaceholder,
  CueDateTimePickerFormatSettings,
  CueDateTimePickerIncrementalSteps,
} from './cue-datetimepicker.types';

export type CueDateTimePart = 'year' | 'month' | 'day' | 'hour' | 'minute' | 'second' | 'millisecond';

export interface CueDateTimePickerCalendarCell {
  date: Date;
  disabled: boolean;
  label: string;
  outsideCurrentPeriod: boolean;
  selected: boolean;
  today: boolean;
}

const FORMAT_TOKENS = /yyyy|yyy|yy|y|MMMM|MMM|MM|M|dd|d|HH|H|hh|h|mm|m|ss|s|SSS|a/g;

const FORMAT_ALIASES: Record<string, string> = {
  d: 'shortDate',
  D: 'fullDate',
  t: 'shortTime',
  T: 'mediumTime',
  g: 'short',
  G: 'medium',
};

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function cloneDate(value: Date): Date {
  return new Date(value.getTime());
}

function numberInRange(value: number, minimum: number, maximum: number, autoCorrect: boolean): number | null {
  if (Number.isNaN(value)) {
    return null;
  }

  if (!autoCorrect && (value < minimum || value > maximum)) {
    return null;
  }

  return Math.min(maximum, Math.max(minimum, value));
}

function resolveTwoDigitYear(year: number, twoDigitYearMax: number): number {
  return year <= twoDigitYearMax ? 2000 + year : 1900 + year;
}

export function resolveDateTimeFormat(format: string | CueDateTimePickerFormatSettings, focused: boolean): string {
  const value = typeof format === 'string' ? format : focused ? format.inputFormat : format.displayFormat;
  return FORMAT_ALIASES[value] ?? value;
}

export function formatDateTime(
  value: Date | null,
  format: string | CueDateTimePickerFormatSettings,
  locale: string,
  focused: boolean
): string {
  if (!isValidDate(value)) {
    return '';
  }

  return formatDate(value, resolveDateTimeFormat(format, focused), locale);
}

export function parseDateTime(
  text: string,
  format: string | CueDateTimePickerFormatSettings,
  fallback: Date,
  twoDigitYearMax: number,
  autoCorrect: boolean,
  locale = 'en-US'
): Date | null {
  const trimmed = text.trim();
  if (!trimmed) {
    return null;
  }

  const resolvedFormat = resolveDateTimeFormat(format, true);
  if (resolvedFormat in FORMAT_ALIASES || Object.values(FORMAT_ALIASES).includes(resolvedFormat)) {
    const parsed = new Date(trimmed);
    return isValidDate(parsed) ? parsed : null;
  }

  const tokens: string[] = [];
  let pattern = '^';
  let cursor = 0;

  for (const match of resolvedFormat.matchAll(FORMAT_TOKENS)) {
    const token = match[0];
    const index = match.index ?? 0;
    pattern += escapeRegExp(resolvedFormat.slice(cursor, index)).replace(/\\ /g, '\\s+');
    tokens.push(token);

    if (token === 'a') {
      pattern += '(AM|PM|am|pm)';
    } else if (token === 'MMM' || token === 'MMMM') {
      pattern += '([\\p{L}.]+)';
    } else {
      pattern += token === 'yyyy' || token === 'yyy' ? '(\\d{3,4})' : '(\\d{1,3})';
    }

    cursor = index + token.length;
  }

  pattern += escapeRegExp(resolvedFormat.slice(cursor)).replace(/\\ /g, '\\s+') + '$';

  let match: RegExpMatchArray | null;
  try {
    match = trimmed.match(new RegExp(pattern, 'u'));
  } catch {
    match = null;
  }

  if (!match || tokens.length === 0) {
    return null;
  }

  const parts: Partial<Record<CueDateTimePart | 'period', number | string>> = {};
  for (let index = 0; index < tokens.length; index += 1) {
    const token = tokens[index];
    const rawValue = match[index + 1];

    if (token === 'a') {
      parts.period = rawValue.toLowerCase();
      continue;
    }

    if (token === 'MMM' || token === 'MMMM') {
      const normalizedValue = rawValue.toLocaleLowerCase(locale).replace(/\./g, '');
      const monthFormatter = new Intl.DateTimeFormat(locale, {
        month: token === 'MMM' ? 'short' : 'long',
      });
      const contextualMonthFormatter = new Intl.DateTimeFormat(locale, {
        day: 'numeric',
        month: token === 'MMM' ? 'short' : 'long',
      });
      const monthIndex = range(12).findIndex((month) => {
        const date = new Date(2000, month, 1);
        const monthNames = [
          monthFormatter.format(date),
          contextualMonthFormatter.formatToParts(date).find((part) => part.type === 'month')?.value ?? '',
        ];
        return monthNames.some(
          (monthName) => monthName.toLocaleLowerCase(locale).replace(/\./g, '') === normalizedValue
        );
      });
      if (monthIndex < 0) {
        return null;
      }
      parts.month = monthIndex + 1;
      continue;
    }

    const numericValue = Number(rawValue);
    if (token.startsWith('y')) {
      parts.year = rawValue.length <= 2 ? resolveTwoDigitYear(numericValue, twoDigitYearMax) : numericValue;
    } else if (token.startsWith('M')) {
      parts.month = numericValue;
    } else if (token.startsWith('d')) {
      parts.day = numericValue;
    } else if (token.startsWith('H') || token.startsWith('h')) {
      parts.hour = numericValue;
    } else if (token.startsWith('m')) {
      parts.minute = numericValue;
    } else if (token.startsWith('s')) {
      parts.second = numericValue;
    } else if (token === 'SSS') {
      parts.millisecond = numericValue;
    }
  }

  const result = cloneDate(fallback);
  const month = numberInRange(Number(parts.month ?? result.getMonth() + 1), 1, 12, autoCorrect);
  const year = numberInRange(Number(parts.year ?? result.getFullYear()), 1, 9999, autoCorrect);
  const day = numberInRange(Number(parts.day ?? result.getDate()), 1, 31, autoCorrect);
  const minute = numberInRange(Number(parts.minute ?? result.getMinutes()), 0, 59, autoCorrect);
  const second = numberInRange(Number(parts.second ?? result.getSeconds()), 0, 59, autoCorrect);
  const millisecond = numberInRange(Number(parts.millisecond ?? result.getMilliseconds()), 0, 999, autoCorrect);
  let hour = numberInRange(Number(parts.hour ?? result.getHours()), 0, 23, autoCorrect);

  if ([month, year, day, hour, minute, second, millisecond].some((part) => part === null)) {
    return null;
  }

  if (/[h]/.test(resolvedFormat)) {
    const twelveHour = numberInRange(Number(parts.hour ?? 12), 1, 12, autoCorrect);
    if (twelveHour === null) {
      return null;
    }
    hour = (twelveHour % 12) + (parts.period === 'pm' ? 12 : 0);
  }

  result.setFullYear(year!, month! - 1, 1);
  const maximumDay = new Date(year!, month!, 0).getDate();
  if (!autoCorrect && day! > maximumDay) {
    return null;
  }
  result.setDate(Math.min(day!, maximumDay));
  result.setHours(hour!, minute!, second!, millisecond!);

  return isValidDate(result) ? result : null;
}

export function autoFillDateTime(
  text: string,
  format: string | CueDateTimePickerFormatSettings,
  fallback: Date,
  twoDigitYearMax: number,
  autoCorrect: boolean,
  locale = 'en-US'
): Date | null {
  const resolvedFormat = resolveDateTimeFormat(format, true);
  const values = text.match(/\d+|am|pm/gi) ?? [];
  let numericIndex = 0;

  const completedText = resolvedFormat.replace(FORMAT_TOKENS, (token) => {
    if (token === 'a') {
      return values.find((value) => /^(am|pm)$/i.test(value)) ?? (fallback.getHours() >= 12 ? 'PM' : 'AM');
    }

    while (numericIndex < values.length && /^(am|pm)$/i.test(values[numericIndex])) {
      numericIndex += 1;
    }
    const enteredValue = values[numericIndex];
    if (enteredValue !== undefined) {
      numericIndex += 1;
      return enteredValue;
    }

    if (token.startsWith('y')) {
      return token.length === 2 ? String(fallback.getFullYear()).slice(-2) : String(fallback.getFullYear());
    }
    if (token.startsWith('M')) return String(fallback.getMonth() + 1);
    if (token.startsWith('d')) return String(fallback.getDate());
    if (token.startsWith('H')) return String(fallback.getHours());
    if (token.startsWith('h')) return String(fallback.getHours() % 12 || 12);
    if (token.startsWith('m')) return String(fallback.getMinutes());
    if (token.startsWith('s')) return String(fallback.getSeconds());
    if (token === 'SSS') return String(fallback.getMilliseconds());
    return token;
  });

  return parseDateTime(completedText, resolvedFormat, fallback, twoDigitYearMax, autoCorrect, locale);
}

export function formatPlaceholder(
  format: string | CueDateTimePickerFormatSettings,
  placeholder: CueDateTimePickerFormatPlaceholder
): string {
  const resolvedFormat = resolveDateTimeFormat(format, true);
  if (placeholder === 'formatPattern') {
    return resolvedFormat;
  }

  const labels =
    typeof placeholder === 'object'
      ? placeholder
      : {
          year: placeholder === 'wide' ? 'year' : 'yr',
          month: placeholder === 'wide' ? 'month' : 'mo',
          day: placeholder === 'wide' ? 'day' : 'day',
          hour: placeholder === 'wide' ? 'hour' : 'hr',
          minute: placeholder === 'wide' ? 'minute' : 'min',
          second: placeholder === 'wide' ? 'second' : 'sec',
          millisecond: 'ms',
        };

  return resolvedFormat.replace(FORMAT_TOKENS, (token) => {
    if (token.startsWith('y')) return labels.year ?? token;
    if (token.startsWith('M')) return labels.month ?? token;
    if (token.startsWith('d')) return labels.day ?? token;
    if (token.startsWith('H') || token.startsWith('h')) return labels.hour ?? token;
    if (token.startsWith('m')) return labels.minute ?? token;
    if (token.startsWith('s')) return labels.second ?? token;
    if (token === 'SSS') return labels.millisecond ?? token;
    return token;
  });
}

export function isValidDate(value: unknown): value is Date {
  return value instanceof Date && !Number.isNaN(value.getTime());
}

export function sameDate(left: Date | null, right: Date | null): boolean {
  return left?.getTime() === right?.getTime();
}

export function sameCalendarDay(left: Date, right: Date): boolean {
  return (
    left.getFullYear() === right.getFullYear() &&
    left.getMonth() === right.getMonth() &&
    left.getDate() === right.getDate()
  );
}

export function startOfDay(value: Date): Date {
  const result = cloneDate(value);
  result.setHours(0, 0, 0, 0);
  return result;
}

export function endOfDay(value: Date): Date {
  const result = cloneDate(value);
  result.setHours(23, 59, 59, 999);
  return result;
}

export function clampDate(value: Date, min: Date | null | undefined, max: Date | null | undefined): Date {
  if (isValidDate(min) && value < min) {
    return cloneDate(min);
  }
  if (isValidDate(max) && value > max) {
    return cloneDate(max);
  }
  return cloneDate(value);
}

export function isDateDisabled(value: Date, disabledDates: CueDateTimePickerDisabledDates): boolean {
  if (typeof disabledDates === 'function') {
    return disabledDates(value);
  }

  if (!disabledDates.length) {
    return false;
  }

  if (disabledDates.every((item) => typeof item === 'number')) {
    return (disabledDates as number[]).includes(value.getDay());
  }

  return (disabledDates as Date[]).some((date) => isValidDate(date) && sameCalendarDay(date, value));
}

export function createMonthCalendar(
  displayedMonth: Date,
  firstDayOfWeek: number,
  selected: Date | null,
  min: Date | null | undefined,
  max: Date | null | undefined,
  disabledDates: CueDateTimePickerDisabledDates,
  showOtherMonthDays: boolean
): CueDateTimePickerCalendarCell[][] {
  const firstOfMonth = new Date(displayedMonth.getFullYear(), displayedMonth.getMonth(), 1);
  const offset = (firstOfMonth.getDay() - firstDayOfWeek + 7) % 7;
  const firstCell = new Date(firstOfMonth);
  firstCell.setDate(firstCell.getDate() - offset);
  const today = new Date();
  const rows: CueDateTimePickerCalendarCell[][] = [];

  for (let rowIndex = 0; rowIndex < 6; rowIndex += 1) {
    const row: CueDateTimePickerCalendarCell[] = [];
    for (let columnIndex = 0; columnIndex < 7; columnIndex += 1) {
      const date = new Date(firstCell);
      date.setDate(firstCell.getDate() + rowIndex * 7 + columnIndex);
      const outsideCurrentPeriod = date.getMonth() !== displayedMonth.getMonth();
      const outsideRange = (isValidDate(min) && endOfDay(date) < min) || (isValidDate(max) && startOfDay(date) > max);
      row.push({
        date,
        disabled: outsideRange || isDateDisabled(date, disabledDates),
        label: outsideCurrentPeriod && !showOtherMonthDays ? '' : String(date.getDate()),
        outsideCurrentPeriod,
        selected: isValidDate(selected) && sameCalendarDay(selected, date),
        today: sameCalendarDay(today, date),
      });
    }
    rows.push(row);
  }

  return rows;
}

export function getIsoWeekNumber(value: Date): number {
  const date = new Date(Date.UTC(value.getFullYear(), value.getMonth(), value.getDate()));
  const day = date.getUTCDay() || 7;
  date.setUTCDate(date.getUTCDate() + 4 - day);
  const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
  return Math.ceil(((date.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
}

export function range(length: number, step = 1, start = 0): number[] {
  const normalizedStep = Math.max(1, Math.floor(step));
  const values: number[] = [];
  for (let value = start; value < length; value += normalizedStep) {
    values.push(value);
  }
  return values;
}

export function getStep(steps: CueDateTimePickerIncrementalSteps | undefined, part: CueDateTimePart): number {
  return Math.max(1, Math.floor(steps?.[part] ?? 1));
}

export function incrementDatePart(
  value: Date,
  part: CueDateTimePart,
  amount: number,
  steps: CueDateTimePickerIncrementalSteps | undefined
): Date {
  const result = cloneDate(value);
  const increment = amount * getStep(steps, part);

  switch (part) {
    case 'year':
      result.setFullYear(result.getFullYear() + increment);
      break;
    case 'month':
      result.setMonth(result.getMonth() + increment);
      break;
    case 'day':
      result.setDate(result.getDate() + increment);
      break;
    case 'hour':
      result.setHours(result.getHours() + increment);
      break;
    case 'minute':
      result.setMinutes(result.getMinutes() + increment);
      break;
    case 'second':
      result.setSeconds(result.getSeconds() + increment);
      break;
    case 'millisecond':
      result.setMilliseconds(result.getMilliseconds() + increment);
      break;
  }

  return result;
}

export function datePartAtCaret(format: string, caretPosition: number): CueDateTimePart {
  for (const match of format.matchAll(FORMAT_TOKENS)) {
    const start = match.index ?? 0;
    const end = start + match[0].length;
    if (caretPosition >= start && caretPosition <= end) {
      const token = match[0];
      if (token.startsWith('y')) return 'year';
      if (token.startsWith('M')) return 'month';
      if (token.startsWith('d')) return 'day';
      if (token.startsWith('H') || token.startsWith('h')) return 'hour';
      if (token.startsWith('m')) return 'minute';
      if (token.startsWith('s')) return 'second';
      if (token === 'SSS') return 'millisecond';
    }
  }

  return 'minute';
}
